// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

export interface AffiliateEarning {
  id: string;
  affiliate_id: string;
  amount: number;
  commission_rate: number;
  source_type: string;
  source_id: string | null;
  status: 'pending' | 'paid' | 'cancelled';
  earned_at: string;
  paid_at: string | null;
  affiliate_name: string;
}

export interface AffiliatePayout {
  id: string;
  affiliate_id: string;
  amount: number;
  payout_method: string;
  payout_reference: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  requested_at: string;
  processed_at: string | null;
  completed_at: string | null;
  notes: string | null;
  affiliate_name: string;
}

export interface AffiliateFinanceStats {
  total_payouts: number;
  pending_payouts: number;
  average_commission: number;
  active_affiliates: number;
}

export const useAffiliateFinanceData = () => {
  const [earnings, setEarnings] = useState<AffiliateEarning[]>([]);
  const [payouts, setPayouts] = useState<AffiliatePayout[]>([]);
  const [stats, setStats] = useState<AffiliateFinanceStats>({
    total_payouts: 0,
    pending_payouts: 0,
    average_commission: 0,
    active_affiliates: 0
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchEarnings = async () => {
    try {
      const { data, error } = await supabase
        .from('affiliate_earnings')
        .select(`
          *,
          profiles!affiliate_earnings_affiliate_id_fkey(name)
        `)
        .order('earned_at', { ascending: false });

      if (error) {
        console.error('Error fetching affiliate earnings:', error);
        throw error;
      }

      const mappedEarnings = data?.map(earning => ({
        ...earning,
        affiliate_name: Array.isArray(earning.profiles) 
          ? earning.profiles[0]?.name || 'Unknown'
          : earning.profiles?.name || 'Unknown'
      })) || [];

      setEarnings(mappedEarnings);
    } catch (error) {
      console.error('Error in fetchEarnings:', error);
      toast({
        title: "Error",
        description: "Failed to load affiliate earnings",
        variant: "destructive",
      });
    }
  };

  const fetchPayouts = async () => {
    try {
      const { data, error } = await supabase
        .from('affiliate_payouts')
        .select(`
          *,
          profiles!affiliate_payouts_affiliate_id_fkey(name)
        `)
        .order('requested_at', { ascending: false });

      if (error) {
        console.error('Error fetching affiliate payouts:', error);
        throw error;
      }

      const mappedPayouts = data?.map(payout => ({
        ...payout,
        affiliate_name: Array.isArray(payout.profiles) 
          ? payout.profiles[0]?.name || 'Unknown'
          : payout.profiles?.name || 'Unknown'
      })) || [];

      setPayouts(mappedPayouts);
    } catch (error) {
      console.error('Error in fetchPayouts:', error);
      toast({
        title: "Error",
        description: "Failed to load affiliate payouts",
        variant: "destructive",
      });
    }
  };

  const fetchStats = async () => {
    try {
      // Get total payouts (completed)
      const { data: completedPayouts, error: payoutsError } = await supabase
        .from('affiliate_payouts')
        .select('amount')
        .eq('status', 'completed');

      if (payoutsError) throw payoutsError;

      // Get pending payouts
      const { data: pendingPayouts, error: pendingError } = await supabase
        .from('affiliate_payouts')
        .select('amount')
        .eq('status', 'pending');

      if (pendingError) throw pendingError;

      // Get active affiliates count
      const { count: activeAffiliatesCount, error: affiliatesError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('role', 'affiliate')
        .eq('status', 'active');

      if (affiliatesError) throw affiliatesError;

      // Calculate average commission rate
      const { data: earnings, error: earningsError } = await supabase
        .from('affiliate_earnings')
        .select('commission_rate');

      if (earningsError) throw earningsError;

      const totalPayouts = completedPayouts?.reduce((sum, payout) => sum + Number(payout.amount), 0) || 0;
      const pendingPayoutsAmount = pendingPayouts?.reduce((sum, payout) => sum + Number(payout.amount), 0) || 0;
      const averageCommission = earnings?.length > 0 
        ? earnings.reduce((sum, earning) => sum + Number(earning.commission_rate), 0) / earnings.length 
        : 0;

      setStats({
        total_payouts: totalPayouts,
        pending_payouts: pendingPayoutsAmount,
        average_commission: averageCommission,
        active_affiliates: activeAffiliatesCount || 0
      });
    } catch (error) {
      console.error('Error in fetchStats:', error);
      toast({
        title: "Error",
        description: "Failed to load affiliate finance stats",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEarnings(), fetchPayouts(), fetchStats()]);
      setLoading(false);
    };

    loadData();
  }, []);

  return {
    earnings,
    payouts,
    stats,
    loading,
    refetch: async () => {
      setLoading(true);
      await Promise.all([fetchEarnings(), fetchPayouts(), fetchStats()]);
      setLoading(false);
    }
  };
};
